import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { deleteOrganization } from "../../../../api/application/organization";
import type { Organization } from "../../../../api/infrastructure/organization.formatter";
import StatsCard from "../../../../components/utils/StatsCard/StatsCard";
import useGetOrganizations from "../../../hooks/useGetOrganizations";
import { syncOpenModalDialog } from "../../../shared/global-tools/dialog-system";
import { showToast } from "../../../shared/global-tools/toast-system";
import Navbar from "../../parts/Navbar/Navbar";
import makeConfirmDialog from "../../utils/ConfirmDialog/ConfirmDialog";
import Icon from "../../utils/Icons/Icon";
import OurDataTable, { type ColumnType } from "../../utils/OurDataTable/OurDataTable";
import SearchInput from "../../utils/SearchInput/SearchInput";
import Spinner from "../../utils/Spinner/Spinner";
import "./index.scss";

export default function OrganizationListPage() {
  const { organizations, loadingOrganizations, refreshOrganizations, searchOrganizations } =
    useGetOrganizations();
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const wrapAsync = useWrapAsync();

  const handleOrganizationDeletion = wrapAsync(async (org: Organization) => {
    try {
      await deleteOrganization(org.id);
      await refreshOrganizations({ limit: 10, page: 1 });
      showToast("Structure supprimée avec succès", { severity: "success" });
    } catch (error) {
      console.error("Erreur lors de la suppression de la structure :", error);
      showToast("Échec de la suppression de la structure", { severity: "error" });
    }
  });

  const editActionBodyTemplate = (org: Organization) => (
    <div style={{ display: "flex", gap: "15px" }}>
      <button
        type="button"
        className="IconBtn"
        onClick={() => navigate(`/app/organizations/${org.id}/edit`)}
      >
        <Icon name="pencil" color="#1F4878" />
      </button>

      <button
        type="button"
        className="IconBtn"
        onClick={() =>
          syncOpenModalDialog(makeConfirmDialog, {
            onConfirm: () => handleOrganizationDeletion(org),
            warningMessage: "Voulez-vous vraiment supprimer cette structure?",
          })
        }
      >
        <Icon name="trash" color="#EF0404" />
      </button>
    </div>
  );
  const localIdBodyTemplate = (_organization: Organization, options: { rowIndex: number }) => (
    <>{options.rowIndex + 1}</>
  );
  const columns: ColumnType<Organization>[] = [
    {
      field: "editAction",
      header: "N°",
      body: localIdBodyTemplate,
    },
    {
      field: "logo",
      header: "",
      body: (org) =>
        org.logo && (
          <img
            className="OrganizationListPage-logo"
            src={org.logo}
            alt={org.name}
            style={{ maxHeight: "50px", width: "auto", objectFit: "contain" }}
          />
        ),
    },
    {
      field: "name",
      header: "Structure",
      body: (org) => (
        <div className="OrganizationListPage-nameColumn">
          {org.name}
          {org.createdAt && <p>Créé le {new Date(org.createdAt).toLocaleDateString("fr")}</p>}
        </div>
      ),
    },
    {
      field: "acronym",
      header: "Sigle",
    },
    {
      field: "ownerName",
      header: "Responsable",
      body: (org) =>
        org.ownerName && (
          <div className="OrganizationListPage-ownerNameColumn">
            {org.ownerName}
            {org.ownerEmail && <p>{org.ownerEmail}</p>}
          </div>
        ),
    },
    {
      field: "enabled",
      header: "Statut",
      body: (org) => (
        <div className={`OrganizationListPage-status ${org.enabled ? "active" : "inactive"}`}>
          {org.enabled ? "Active" : "Inactive"}
        </div>
      ),
    },
    {
      field: "editAction",
      header: "Actions",
      body: editActionBodyTemplate,
    },
  ];

  return (
    <div className="OrganizationListPage">
      <Navbar pageTitle="Liste des structures sous tutelle" />

      <div className="OrganizationListPage-content Container">
        <div className="OrganizationListPage-cards">
          <StatsCard
            id="active-organizations"
            label="Actives"
            total={organizations.filter((org) => org.enabled).length}
            date={new Date().toISOString()}
          />
          <StatsCard
            id="inactive-organizations"
            label="Inactives"
            total={organizations.filter((org) => !org.enabled).length}
            date={new Date().toISOString()}
          />
        </div>

        {loadingOrganizations ? (
          <Spinner />
        ) : (
          <div className="OrganizationListPage-dataTableContainer">
            <div className="OrganizationListPage-dataTableHeader">
              <Button
                className="Btn sm primary"
                onClick={() => {
                  navigate("/app/organizations/create");
                }}
                label="Ajouter"
              />

              <SearchInput
                className="OrganizationListPage-searchInput"
                size="sm"
                value={searchTerm}
                onChange={(value) => setSearchTerm(value)}
                onSearch={(val) => {
                  searchOrganizations(val);
                }}
                onClear={() => {
                  refreshOrganizations({
                    limit: 10,
                    page: 1,
                  });
                }}
              />
            </div>

            <OurDataTable<Organization>
              data={organizations}
              columns={columns}
              options={{
                rows: 10,
                stripedRows: true,
                tableStyle: { minWidth: "40rem" },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
